/** A corner/center alignment pair, independent of any specific composable. */
export type GeometryAlign = {
  horizontal: "left" | "center" | "right";
  vertical: "top" | "center" | "bottom";
};

/** A rectangular clipping region (viewport or a scrollable container's rect). */
export type GeometryBounds = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export type GeometryCollisionType = "fit" | "flip";

export type GeometryCollision = {
  horizontal: GeometryCollisionType;
  vertical: GeometryCollisionType;
};

export type ComputeCollisionOffsetInput = {
  /** The anchor rect (or a zero-size point rect for a click/keyboard activation). */
  anchor: DOMRect;
  /** The popup's own measured size, or `null` before it has been measured. */
  popup: DOMRect | null;
  /** Point on the anchor that the popup is positioned from. */
  anchorAlign: GeometryAlign;
  /** Point on the popup that is placed at the computed anchor point. */
  popupAlign: GeometryAlign;
  /** Clipping bounds checked for overflow; same coordinate space as `anchor`. */
  bounds: GeometryBounds;
  /** Per-axis collision behavior applied when the preferred corner overflows. */
  collision: GeometryCollision;
};

const flipHorizontal = (
  horizontal: GeometryAlign["horizontal"],
): GeometryAlign["horizontal"] =>
  horizontal === "left" ? "right" : horizontal === "right" ? "left" : "center";

const flipVertical = (
  vertical: GeometryAlign["vertical"],
): GeometryAlign["vertical"] =>
  vertical === "top" ? "bottom" : vertical === "bottom" ? "top" : "center";

const computeCorner = (
  anchor: DOMRect,
  popup: DOMRect | null,
  anchorAlign: GeometryAlign,
  popupAlign: GeometryAlign,
) => {
  const anchorX =
    anchor.left +
    (anchorAlign.horizontal === "right"
      ? anchor.width
      : anchorAlign.horizontal === "center"
        ? anchor.width / 2
        : 0);
  const anchorY =
    anchor.top +
    (anchorAlign.vertical === "bottom"
      ? anchor.height
      : anchorAlign.vertical === "center"
        ? anchor.height / 2
        : 0);

  const popupOffsetX = popup
    ? popupAlign.horizontal === "right"
      ? popup.width
      : popupAlign.horizontal === "center"
        ? popup.width / 2
        : 0
    : 0;
  const popupOffsetY = popup
    ? popupAlign.vertical === "bottom"
      ? popup.height
      : popupAlign.vertical === "center"
        ? popup.height / 2
        : 0
    : 0;

  return { left: anchorX - popupOffsetX, top: anchorY - popupOffsetY };
};

const fitAxis = (
  position: number,
  size: number,
  start: number,
  end: number,
) => {
  if (size >= end - start) return start;
  return Math.min(Math.max(position, start), end - size);
};

/**
 * Computes a popup's top-left corner from an anchor rect and alignment pair,
 * then applies Kendo-style `fit` or `flip` collision behavior independently
 * on each axis. Fit shifts the popup within bounds without changing sides;
 * flip moves it to the anchor's opposite side only when that reduces overflow.
 *
 * Returns the preferred position when `popup` is `null`, since collision
 * behavior cannot be calculated before the popup has been measured.
 */
export const computeCollisionOffset = ({
  anchor,
  popup,
  anchorAlign,
  popupAlign,
  bounds,
  collision,
}: ComputeCollisionOffsetInput): { left: number; top: number } => {
  const preferred = computeCorner(anchor, popup, anchorAlign, popupAlign);
  if (!popup) return preferred;

  let { left, top } = preferred;

  if (collision.horizontal === "fit") {
    left = fitAxis(left, popup.width, bounds.left, bounds.right);
  } else {
    const overflowsRight = left + popup.width - bounds.right;
    const overflowsLeft = bounds.left - left;
    if (overflowsRight > 0 || overflowsLeft > 0) {
      const flipped = computeCorner(
        anchor,
        popup,
        {
          horizontal: flipHorizontal(anchorAlign.horizontal),
          vertical: anchorAlign.vertical,
        },
        {
          horizontal: flipHorizontal(popupAlign.horizontal),
          vertical: popupAlign.vertical,
        },
      );
      const currentOverflow =
        Math.max(overflowsRight, 0) + Math.max(overflowsLeft, 0);
      const flippedOverflow =
        Math.max(flipped.left + popup.width - bounds.right, 0) +
        Math.max(bounds.left - flipped.left, 0);
      if (flippedOverflow < currentOverflow) left = flipped.left;
    }
  }

  if (collision.vertical === "fit") {
    top = fitAxis(top, popup.height, bounds.top, bounds.bottom);
  } else {
    const overflowsBottom = top + popup.height - bounds.bottom;
    const overflowsTop = bounds.top - top;
    if (overflowsBottom > 0 || overflowsTop > 0) {
      const flipped = computeCorner(
        anchor,
        popup,
        {
          horizontal: anchorAlign.horizontal,
          vertical: flipVertical(anchorAlign.vertical),
        },
        {
          horizontal: popupAlign.horizontal,
          vertical: flipVertical(popupAlign.vertical),
        },
      );
      const currentOverflow =
        Math.max(overflowsBottom, 0) + Math.max(overflowsTop, 0);
      const flippedOverflow =
        Math.max(flipped.top + popup.height - bounds.bottom, 0) +
        Math.max(bounds.top - flipped.top, 0);
      if (flippedOverflow < currentOverflow) top = flipped.top;
    }
  }

  return { left, top };
};
